import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { usePubNub } from "pubnub-react";
import Pubnub from "pubnub";
import map from "lodash/map";
import filter from "lodash/filter";
import size from "lodash/size";

import { Trip } from "../../../types";
import { useOperator } from "../../../globals/hooks/useOperator";
import { LOAD_VEHICLE_GPS_QUERY } from "../../../globals/graphql";
import { PubNubMessage, TrackedDriver } from "../types";

const PROVIDER_SUPPRESSION_MS = 30_000;

function useTrackedDriver(trips: Trip[]) {
  // hooks
  const pubnub = usePubNub();
  const { operator } = useOperator();

  // refs
  const hasInitialized = useRef(false);
  const lastProviderTimestamp = useRef<Map<string, number>>(new Map());

  // state
  const [trackedDrivers, setTrackedDrivers] = useState<
    Map<string, TrackedDriver>
  >(new Map());

  // derived state
  const activeOrUpcomingTrips = useMemo(() => {
    const activeTrips = filter(
      trips,
      ({ routes }) =>
        routes?.[0]?.dispatchStatus !== "done" &&
        routes?.[0]?.dispatchStatus !== "pdo"
    );

    return new Map(map(activeTrips, (trip) => [trip.id, trip]));
  }, [trips]);

  const routeIds = useMemo(() => {
    const routeIdArray = [];
    for (const trip of activeOrUpcomingTrips.values()) {
      const routeId = trip.routes?.[0]?.id;
      if (!routeId) continue;
      routeIdArray.push(routeId);
    }
    return routeIdArray;
  }, [activeOrUpcomingTrips]);

  // Only poll GPS for one route at a time — other trips fall back to PubNub
  const activeRouteId = useMemo(() => {
    for (const trip of activeOrUpcomingTrips.values()) {
      const route = trip.routes?.[0];
      if (route?.id && route?.vehicle?.id) return route.id;
    }
    return null;
  }, [activeOrUpcomingTrips]);

  // poll provider GPS for the active route's vehicle
  const { data: gpsData } = useQuery(LOAD_VEHICLE_GPS_QUERY, {
    variables: { routeId: activeRouteId },
    skip: !activeRouteId,
    pollInterval: 5000,
  });

  // process provider GPS into tracked drivers
  useEffect(() => {
    const providerGPS = gpsData?.loadVehicleGPS;
    if (!providerGPS) return;

    for (const [tripId, trip] of activeOrUpcomingTrips.entries()) {
      const route = trip.routes?.[0];
      if (!route || route.id !== activeRouteId) continue;

      lastProviderTimestamp.current.set(tripId, Date.now());

      const vehicleId = route.vehicle?.id;
      const driverId = route.driver?.id ?? `vehicle-${vehicleId}`;

      setTrackedDrivers((prev) => {
        const updated = new Map(prev);
        updated.set(tripId, {
          ...(route.driver ?? { id: driverId }),
          dispatchStatus: route.dispatchStatus,
          location: {
            coords: {
              latitude: providerGPS.latitude,
              longitude: providerGPS.longitude,
              altitude: null,
              accuracy: null,
              altitudeAccuracy: null,
              heading: providerGPS.heading ?? null,
              speed: providerGPS.speed ?? null,
            },
            timestamp: new Date(providerGPS.recordedAt).getTime(),
          },
        });
        return updated;
      });
      break;
    }
  }, [gpsData, activeOrUpcomingTrips, activeRouteId]);

  // event handlers
  // handle incoming pubnub messages
  const handleMessage = useCallback(
    (messageEvent: Pubnub.MessageEvent) => {
      const { tripId, location }: PubNubMessage = messageEvent.message;

      const activeTrip = activeOrUpcomingTrips.get(tripId);

      if (!activeTrip) return;

      // suppress driver messages if provider tracking is active for this trip
      const lastProvider = lastProviderTimestamp.current.get(tripId);
      if (lastProvider && Date.now() - lastProvider < PROVIDER_SUPPRESSION_MS) {
        return;
      }

      const route = activeTrip.routes?.[0];
      if (!route) return;

      const driverId = route.driver?.id ?? `vehicle-${tripId}`;

      setTrackedDrivers((prevTrackedDrivers) => {
        prevTrackedDrivers.set(tripId, {
          location,
          ...(route.driver ?? { id: driverId }),
          dispatchStatus: route.dispatchStatus,
        });

        return new Map(prevTrackedDrivers);
      });
    },
    [activeOrUpcomingTrips]
  );

  // effects
  // initialize
  useEffect(() => {
    // prevent initializing more than once,
    // but wait for incoming trips query to initialize
    if (hasInitialized.current || !size(activeOrUpcomingTrips)) return;
    hasInitialized.current = true;

    const channels = [operator.id, ...routeIds];
    // call pubnub to get most recent 100 messages.
    // filter for unique drivers that are included in trips
    // (and the trip is not closed)
    const fetchMessages = async () => {
      const response = await pubnub.fetchMessages({ channels });
      const messages = response.channels[channels[0]];

      if (!messages) return;

      messages.forEach(({ message }: { message: PubNubMessage }) => {
        const { tripId, location } = message;

        const activeTrip = activeOrUpcomingTrips.get(tripId);
        const route = activeTrip?.routes?.[0];
        if (!activeTrip || !route) return;

        trackedDrivers.set(tripId, {
          location,
          ...(route.driver ?? { id: `vehicle-${tripId}` }),
          dispatchStatus: route.dispatchStatus,
        });
      });

      setTrackedDrivers(new Map(trackedDrivers));
    };

    fetchMessages();
  }, [activeOrUpcomingTrips, operator?.id, pubnub, trackedDrivers, routeIds]);

  // clean up handleMessage callback
  useEffect(() => {
    pubnub.addListener({ message: handleMessage });

    return () => {
      pubnub.removeListener({ message: handleMessage });
    };
  }, [handleMessage, pubnub]);

  // clean up subscription
  useEffect(() => {
    const channels = [operator?.id, ...routeIds];
    pubnub.subscribe({ channels });

    return () => {
      pubnub.unsubscribe({ channels });
    };
  }, [pubnub, operator?.id, routeIds]);

  // sync dispatchStatus from polled trip data into tracked drivers
  useEffect(() => {
    setTrackedDrivers((prev) => {
      let hasChanged = false;
      const updated = new Map(prev);

      updated.forEach((trackedDriver, tripId) => {
        const activeTrip = activeOrUpcomingTrips.get(tripId);
        const route = activeTrip?.routes?.[0];
        if (route && trackedDriver.dispatchStatus !== route.dispatchStatus) {
          updated.set(tripId, {
            ...trackedDriver,
            dispatchStatus: route.dispatchStatus,
          });
          hasChanged = true;
        }
      });

      return hasChanged ? updated : prev;
    });
  }, [activeOrUpcomingTrips]);

  // remove tracked drivers without corresponding active trip
  useEffect(() => {
    setTrackedDrivers((prev) => {
      let hasChanged = false;
      const updated = new Map(prev);

      updated.forEach((_, tripId) => {
        if (!activeOrUpcomingTrips.has(tripId)) {
          updated.delete(tripId);
          hasChanged = true;
        }
      });

      return hasChanged ? updated : prev;
    });
  }, [activeOrUpcomingTrips]);

  return { trackedDriver: [...trackedDrivers.values()] };
}

export { useTrackedDriver };
